/**
 * Shared vocabulary of the communications module.
 *
 * These keys live in the global catalog because they are used both by
 * components and by `utils/campaigns.js`, which has no component instance.
 */

export const campaignsMessagesEn = {
    campaigns: {
        title: "Campaigns",
        betaChip: "Beta",
        campaign: "Campaign",
        status: {
            open: "Open",
            closed: "Closed",
        },
        kind: {
            campaign: "Campaign",
            direct: "Single email",
        },
        sendMode: {
            manual: "Manual",
            automatic: "Automatic",
        },
        sendModeHint: {
            manual: "You choose when each recipient is sent.",
            automatic: "New recipients are sent automatically, within your weekly quota.",
        },
        template: {
            classic: "Classic",
            announcement: "Announcement",
            letter: "Letter",
        },
        selectionMode: {
            primary: "Primary contact",
            documents: "Contacts receiving documents",
            roles: "Contacts with a given role",
            all: "Every contact with an email",
            contacts: "Selected contacts",
        },
        recipientStatus: {
            pending: "Pending",
            queued: "Scheduled",
            sent: "Sent",
            delivered: "Delivered",
            opened: "Opened",
            clicked: "Clicked",
            bounced: "Rejected",
            complained: "Marked as spam",
            failed: "In error",
            skipped_no_email: "No email address",
            skipped_opt_out: "Unsubscribed",
            skipped_duplicate_email: "Duplicate address",
            skipped_no_content: "No matching message",
            skipped_archived: "Archived",
            skipped_bounced: "Rejected address",
            skipped_inactive_contact: "Inactive contact",
        },
        logAction: {
            created: "Created",
            updated: "Updated",
            opened: "Reopened",
            closed: "Closed",
            reopened_auto: "Reopened automatically",
            recipients_added: "Recipients added",
            recipients_removed: "Recipients removed",
            send_launched: "Send launched",
            content_locked: "Message locked",
            contents_reordered: "Messages reordered",
            quota_reached: "Weekly quota reached",
            retry_failed: "Failed recipients retried",
            customer_joined: "Customer connected from an email",
            opted_out: "Contact unsubscribed",
            stale_requeued: "Stalled recipients requeued",
        },
        errors: {
            entity_not_verified:
                "Your account must be verified before sending communications.",
            entity_readonly: "Your account cannot send communications.",
            missing_contact_email:
                "Set a contact email on your company before sending communications.",
            quota_exceeded: "The weekly email quota is reached.",
            campaign_closed: "Reopen the campaign before sending.",
            campaign_not_sendable: "The campaign is not ready to be sent.",
            content_not_sendable: "The message is not ready to be sent.",
            no_content: "Add at least one message.",
            no_sendable_content: "No message is ready to be sent.",
            subject_required: "The subject is required.",
            subject_too_long: "The subject is too long.",
            preheader_too_long: "The preview text is too long.",
            body_required: "The message is empty.",
            body_too_long: "The message is too long.",
            body_empty_after_cleanup:
                "The message contains no text once formatting is cleaned up.",
            links_not_allowed: "Hyperlinks are not allowed in a communication.",
            unknown_variables: "The message uses an unknown variable.",
            too_many_attachments: "Too many attachments.",
            attachments_too_large: "The attachments are too large.",
        },
    },
};

export const campaignsMessagesFr = {
    campaigns: {
        title: "Campagnes",
        betaChip: "Beta",
        campaign: "Campagne",
        status: {
            open: "Ouverte",
            closed: "Fermée",
        },
        kind: {
            campaign: "Campagne",
            direct: "Email ponctuel",
        },
        sendMode: {
            manual: "Manuel",
            automatic: "Automatique",
        },
        sendModeHint: {
            manual: "Vous décidez du moment de l'envoi pour chaque destinataire.",
            automatic:
                "Les nouveaux destinataires sont envoyés automatiquement, dans la limite de votre quota hebdomadaire.",
        },
        template: {
            classic: "Classique",
            announcement: "Annonce",
            letter: "Lettre",
        },
        selectionMode: {
            primary: "Contact principal",
            documents: "Contacts qui reçoivent les documents",
            roles: "Contacts ayant un rôle précis",
            all: "Tous les contacts avec un email",
            contacts: "Contacts sélectionnés",
        },
        recipientStatus: {
            pending: "En attente",
            queued: "Programmé",
            sent: "Envoyé",
            delivered: "Délivré",
            opened: "Ouvert",
            clicked: "Cliqué",
            bounced: "Rejeté",
            complained: "Signalé comme spam",
            failed: "En erreur",
            skipped_no_email: "Pas d'adresse email",
            skipped_opt_out: "Désabonné",
            skipped_duplicate_email: "Adresse en doublon",
            skipped_no_content: "Aucun message correspondant",
            skipped_archived: "Archivé",
            skipped_bounced: "Adresse rejetée",
            skipped_inactive_contact: "Contact inactif",
        },
        logAction: {
            created: "Créée",
            updated: "Modifiée",
            opened: "Réouverte",
            closed: "Fermée",
            reopened_auto: "Réouverte automatiquement",
            recipients_added: "Destinataires ajoutés",
            recipients_removed: "Destinataires retirés",
            send_launched: "Envoi lancé",
            content_locked: "Message verrouillé",
            contents_reordered: "Messages réordonnés",
            quota_reached: "Quota hebdomadaire atteint",
            retry_failed: "Échecs relancés",
            customer_joined: "Client connecté depuis un email",
            opted_out: "Contact désabonné",
            stale_requeued: "Envois bloqués relancés",
        },
        errors: {
            entity_not_verified:
                "Votre compte doit être vérifié avant d'envoyer des communications.",
            entity_readonly: "Votre compte ne peut pas envoyer de communications.",
            missing_contact_email:
                "Renseignez l'email de contact de votre société avant d'envoyer des communications.",
            quota_exceeded: "Le quota hebdomadaire d'emails est atteint.",
            campaign_closed: "Réouvrez la campagne avant d'envoyer.",
            campaign_not_sendable: "La campagne n'est pas prête à être envoyée.",
            content_not_sendable: "Le message n'est pas prêt à être envoyé.",
            no_content: "Ajoutez au moins un message.",
            no_sendable_content: "Aucun message n'est prêt à être envoyé.",
            subject_required: "L'objet est obligatoire.",
            subject_too_long: "L'objet est trop long.",
            preheader_too_long: "Le texte d'aperçu est trop long.",
            body_required: "Le message est vide.",
            body_too_long: "Le message est trop long.",
            body_empty_after_cleanup:
                "Le message ne contient aucun texte une fois la mise en forme nettoyée.",
            links_not_allowed:
                "Les liens hypertextes ne sont pas autorisés dans une communication.",
            unknown_variables: "Le message utilise une variable inconnue.",
            too_many_attachments: "Trop de pièces jointes.",
            attachments_too_large: "Les pièces jointes sont trop volumineuses.",
        },
    },
};
